import React, {ReactElement, useCallback, useMemo} from 'react';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import {
  Typography,
  ColorSwatchGroup,
  ColorSwatch
} from '@customink/pigment-react';
import {ProductVariantsInner} from '@customink/stores-api-client';
import {useGetStoreProductQuery} from '../../api/storesApi';
import {rgbToHex} from '../../utils/helpers';
import './index.scss';

type Props = {
  storeSlug: string;
  productId: number;
  currentVariant: ProductVariantsInner;
  setCurrentVariant: (variant: ProductVariantsInner) => void;
};

type ColorSwatchesProps = {
  variants: ProductVariantsInner[];
  onSwatchSelect: (swatchData: SwatchData) => void;
};

type ColorNameProps = {
  variants: ProductVariantsInner[];
  currentVariantId: number | null;
};

type SwatchData = {
  color: string;
  value: string;
  trimRgb: string;
  id: number;
};

const ColorSwatches: React.FC<ColorSwatchesProps> = ({
  variants,
  onSwatchSelect
}: ColorSwatchesProps): ReactElement => {
  const displayOnly = variants?.length === 1;
  const swatchData = useMemo(
    () =>
      variants.map(variant => {
        const hasTrimRgb =
          variant.trimRgb && typeof variant.trimRgb.r === 'number';
        const rgb = {
          red: variant.rgb?.r || 0,
          green: variant.rgb?.g || 0,
          blue: variant.rgb?.b || 0
        };
        const trimRgb = {
          red: variant.trimRgb?.r,
          green: variant.trimRgb?.g,
          blue: variant.trimRgb?.b
        };
        return {
          color: rgbToHex(rgb),
          value: variant.name,
          trimRgb: hasTrimRgb ? rgbToHex(trimRgb) : undefined,
          id: variant.id
        };
      }),
    [variants]
  );
  return displayOnly ? (
    <ColorSwatch
      className="productColorSwatch"
      size="large"
      displayOnly
      color={swatchData[0].color}
      trimRgb={swatchData[0].trimRgb}
      aria-label={swatchData[0].value}
    />
  ) : (
    <ColorSwatchGroup
      size="large"
      onSwatchSelect={onSwatchSelect}
      swatchData={swatchData}
    />
  );
};

const ColorName: React.FC<ColorNameProps> = ({
  currentVariantId,
  variants
}: ColorNameProps): ReactElement => {
  if (!currentVariantId) return null;
  const variant = variants?.find(v => v.id === currentVariantId);
  const name = variant?.name;
  return variants && variants.length ? (
    <Typography
      className="storeProductColorPicker-colorText"
      variant="subHeading">
      Color: <span className="storeProductColorPicker-nameSpan">{name}</span>
    </Typography>
  ) : (
    <Skeleton width={90} />
  );
};

const StoreProductColorPicker: React.FC<Props> = ({
  storeSlug,
  productId,
  currentVariant,
  setCurrentVariant
}: Props): ReactElement => {
  const {data: product} = useGetStoreProductQuery({
    urlSlug: storeSlug,
    productId
  });
  const variants = useMemo(() => {
    if (product) return product.variants;
    return null;
  }, [product]);

  const currentVariantId = useMemo(() => currentVariant?.id, [currentVariant]);

  const onSwatchSelect = useCallback(
    (swatchData: SwatchData) => {
      setCurrentVariant(variants?.find(v => v.id === swatchData.id));
    },
    [variants]
  );

  return (
    <Stack className="storeProductColorPicker">
      <ColorName currentVariantId={currentVariantId} variants={variants} />
      {variants && variants.length ? (
        <ColorSwatches variants={variants} onSwatchSelect={onSwatchSelect} />
      ) : (
        <Skeleton width={100} />
      )}
    </Stack>
  );
};

export default StoreProductColorPicker;
